import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import { Redirect, Route } from "wouter";

export function ProtectedRoute({
  path,
  component: Component,
  roles,
}: {
  path: string;
  component: React.ComponentType<any>;
  roles?: string[];
}) {
  return (
    <Route path={path}>
      {(params) => (
        <ProtectedContent component={Component} roles={roles} params={params} path={path} />
      )}
    </Route>
  );
}

function ProtectedContent({ component: Component, roles, params, path }: any) {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  // Redirect to password reset if required
  if ((user as any).requiresPasswordReset && path !== "/reset-password") {
    return <Redirect to="/reset-password" />;
  }

  // Check role-based access if roles are specified
  if (roles && !roles.includes(user.role)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
        <p className="text-gray-600 text-center">
          You don't have permission to access this page. This area is restricted to {roles.join(", ")} users.
        </p>
      </div>
    );
  }

  // Check visibleMenus for Customer role
  if (user.role === "Customer" && (user as any).visibleMenus) {
    const isMainRoute = path === "/" || path.startsWith("/analysis") || path.startsWith("/analyses") || path.startsWith("/nyserda") || path.startsWith("/osem-onboarding");
    if (isMainRoute) {
      let allowed = false;
      for (const menu of (user as any).visibleMenus) {
        if (menu === "/") {
          if (path === "/") { allowed = true; break; }
        } else if (path === menu || path.startsWith(`${menu}/`)) {
          allowed = true;
          break;
        }
      }
      if (!allowed) {
        const fallback = (user as any).visibleMenus[0] === "/" ? "/" : ((user as any).visibleMenus[0] || "/profile");
        return <Redirect to={fallback} />;
      }
    }
  }

  return <Component params={params} />;
}
